import { Link } from 'react-router-dom';
import { Pages } from 'constants/menu';
import {
  CampaignAdminModelMarketingStatus,
  CampaignDashboardLaunch,
  CampaignDashboardRow,
} from '@urbanx/agx.campaigns.client';
import type { GetAllAgentsDetailsProjection } from '@urbanx/agx.agencies.client';
import {
  AgxAvatar,
  AgxAvatarLabelGroup,
  AgxBadge,
  AgxSpacer,
  AgxTable,
  AgxTypography,
  IColumnConfig,
  SortOrder,
  getCampaignStageDetails,
} from '@urbanx/agx.ui-components2';
import { cleanTwoLineAddress, PropertyAddress } from '@urbanx/agx-ui-components';
import dayjs from 'dayjs';
import placeHolderImage from 'assets/images/placeholder-property.png';
import LeadAgentCell from 'components/LeadAgentCell';
import { useMemo } from 'react';
import { createAgencyLookup, getAgencyInitials } from 'utils/agencyUtils';
import { getStatusConfig } from '../components/campaignMarketing/CampaignFlowStatusBadge';

const formatLaunchBadge = (launch?: CampaignDashboardLaunch) => {
  if (!launch?.status) {
    return (
      <AgxBadge
        type="colourfill"
        variant="normal"
        size="md"
        text="Launch date not set"
        colour="grey"
      />
    );
  }

  if (launch.status === 'OffMarket') {
    return (
      <AgxBadge type="colourfill" variant="normal" size="md" text="Off Market" colour="grey" />
    );
  }

  if (!launch.date) {
    return (
      <AgxBadge
        type="colourfill"
        variant="normal"
        size="md"
        text="Launch date not set"
        colour="grey"
      />
    );
  }

  return (
    <AgxBadge
      type="colourfill"
      variant="normal"
      size="md"
      text={dayjs(launch.date).format('Do MMM')}
      colour="orange"
    />
  );
};

interface CampaignsDashboardTableProps {
  rows: CampaignDashboardRow[];
  agencyLookup: ReturnType<typeof createAgencyLookup>;
  agentLookup: Record<string, GetAllAgentsDetailsProjection>;
  isPending: boolean;
  isAgenciesPending: boolean;
  lastRowRef: (node: HTMLDivElement | null) => void;
  onSortLaunch: (sortOrder: SortOrder) => void;
}

const CampaignsDashboardTable = ({
  rows,
  agencyLookup,
  agentLookup,
  isPending,
  isAgenciesPending,
  lastRowRef,
  onSortLaunch,
}: CampaignsDashboardTableProps) => {
  const dashboardColumns: (string | IColumnConfig)[] = [
    { name: 'Address', maxColWidth: 250 },
    'Agency',
    'Lead Agent',
    'Status',
    'Contract',
    {
      name: 'Launch',
      sortable: true,
      sortFunction: onSortLaunch,
      defaultSortOrder: 1,
    },
    { align: 'right', name: 'Marketing' },
  ];

  const tableRows = useMemo(() => {
    return rows.map((row, index) => {
      const agencyName = row.agencyId ? agencyLookup[row.agencyId]?.name : undefined;
      const agencyInitials = getAgencyInitials(agencyName);
      const cleanAddress = row.address
        ? cleanTwoLineAddress(row.address as PropertyAddress)
        : ['Unknown address'];
      const [addressLineOne, addressLineTwo] = Array.isArray(cleanAddress)
        ? cleanAddress
        : [cleanAddress];
      const marketingStatusConfig = getStatusConfig(
        row.marketingStatus as CampaignAdminModelMarketingStatus | undefined
      );
      const isLastRow = index === rows.length - 1;
      const leadAgent = row.leadAgentId ? agentLookup[row.leadAgentId] : undefined;
      const campaignStatus = getCampaignStageDetails(row.campaignStatus);

      return {
        Address: (
          <div ref={isLastRow ? lastRowRef : null}>
            <AgxSpacer contentAlignment="start">
              <AgxAvatarLabelGroup
                id={`campaign-dashboard-${index}`}
                label1={addressLineOne ?? 'Unknown address'}
                label2={addressLineTwo ?? ''}
                photoUrl={row.propertyImageUrl ?? placeHolderImage}
                type="avatar"
                variant="custom"
                photoHeight="48px"
                photoWidth="64px"
                size="large"
                truncateLabels
              />
            </AgxSpacer>
          </div>
        ),
        Agency: (
          <AgxSpacer direction="horizontal" gap="spacing-md" contentAlignment="center">
            {row.agencyId && agencyLookup[row.agencyId]?.logoUrl && (
              <AgxAvatar
                type="image"
                size="sm"
                imageUri={agencyLookup[row.agencyId]?.logoUrl ?? ''}
                backgroundColor={agencyLookup[row.agencyId]?.backgroundColor}
              />
            )}
            {row.agencyId && !agencyLookup[row.agencyId]?.logoUrl && (
              <AgxAvatar type="text-dark" size="sm" text={agencyInitials ?? 'UA'} />
            )}

            <AgxTypography
              id={`campaign-dashboard-agency-${index}`}
              size="small"
              type="body_ui"
              color="var(--text-secondary)"
            >
              {agencyName ?? '--'}
            </AgxTypography>
          </AgxSpacer>
        ),
        'Lead Agent': (
          <LeadAgentCell
            id={`campaign-dashboard-lead-agent-${index}`}
            agent={leadAgent}
            mode="letters"
            truncateLabels
          />
        ),
        Status: (
          <AgxBadge
            type="colourfill"
            size="md"
            colour={campaignStatus.color}
            text={campaignStatus.text ?? (row.campaignStatus || '--')}
          />
        ),
        Marketing: marketingStatusConfig ? (
          <AgxBadge
            type="colourfill"
            colour={marketingStatusConfig.badgeColor}
            size="md"
            text={marketingStatusConfig.text}
          />
        ) : (
          '--'
        ),
        Contract: row.blankContractLive ? (
          <AgxBadge type="colourfill" colour="green" size="md" text="Contract Live" />
        ) : (
          <AgxBadge type="colourfill" colour="red" size="md" text="Contract Offline" />
        ),
        Launch: formatLaunchBadge(row.launch),
      };
    });
  }, [agencyLookup, agentLookup, lastRowRef, rows]);

  return (
    <AgxSpacer direction="horizontal" fill>
      <AgxSpacer direction="vertical" gap={'sm'} fill className="campaignDashboardTable">
        <AgxTable
          columns={dashboardColumns}
          rows={tableRows}
          variant={!isPending && tableRows.length ? 'default' : 'minimal'}
          showLoadingState={isPending && tableRows.length === 0}
          tableStyle="square"
          noDataLabel={isAgenciesPending || isPending ? 'Loading...' : 'No campaigns found.'}
          noDataGlyphType="house"
          renderRowLink={(index, linkProps) => {
            const campaign = rows[index];
            if (!campaign.agencyId || !campaign.campaignId) return;
            const address = cleanTwoLineAddress(campaign.address as PropertyAddress);
            return (
              <Link
                {...linkProps}
                to={`/${Pages.AGENCIES}/${campaign.agencyId}/${Pages.CAMPAIGNS}/${campaign.campaignId}/${Pages.CAMPAIGN_OVERVIEW}`}
                aria-label={Array.isArray(address) ? address.join(', ') : address}
              />
            );
          }}
        />
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default CampaignsDashboardTable;
